import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";

/** Kinder, deren Tap dem Kind gehört (Primäraktion schlägt Erklärung). */
const INTERACTIVE_CHILD =
  "button, a, input, select, textarea, [role='button'], [role='checkbox'], [role='switch']";

const HOVER_OPEN_DELAY_MS = 150;

export type InfoTipProps = Omit<ComponentPropsWithoutRef<"span">, "children"> & {
  text: ReactNode;
  children: ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  className?: string;
};

/**
 * Hüllt children in eine Erklär-Blase.
 *
 * **Touch: ein Tap öffnet und schließt sie. Desktop: Hover öffnet, Verlassen
 * schließt.**
 *
 * Bewusst ein Popover statt eines Radix-Tooltips: dessen Trigger öffnet
 * ausschließlich in `onPointerMove` und steigt dort bei `pointerType ===
 * "touch"` sofort wieder aus; der Fokus-Pfad ist nach einem Tap zusätzlich
 * durch Radix' `isPointerDownRef` blockiert, `onClick` schließt explizit, und
 * ein <span> ohne tabIndex ist gar nicht fokussierbar. Auf dem Handy (mobiler
 * Browser wie Capacitor-App) war damit jede Erklärung unerreichbar — in einem
 * Spiel, dessen Kampf-UI komplett an InfoTip hängt.
 *
 * Der Trigger bleibt ein nicht-fokussierbares <span> ohne Button-Rolle:
 * InfoTip umschließt an vielen Stellen bereits Buttons, Links oder Inputs und
 * steht selbst in <label>s — ein zusätzliches interaktives Element wäre dort
 * verschachtelte Interaktivität (A11y- und Hydrations-Problem). Taps auf ein
 * interaktives Kind gehören deshalb dem Kind, nicht der Blase.
 *
 * Anchor mit `asChild`, damit kein zusätzliches Wrapper-Element die Layouts
 * bricht.
 *
 * `forwardRef` + Rest-Props-Spread sind Pflicht, nicht Kosmetik: InfoTip steht
 * an mehreren Stellen selbst als `asChild`-Kind eines Radix-Triggers (z.B.
 * `AlertDialogTrigger` um den "Vergessen"-Button in `_app.game.moves.tsx`).
 * Radix klont das Kind per Slot und übergibt dabei `onClick`, `ref` und
 * aria-/data-Props. Eine einfache Funktionskomponente, die nur
 * text/children/side/className liest, verwirft all das: der Dialog ging nie
 * auf und React loggte "Function components cannot be given refs".
 * Fremd-Handler laufen VOR der eigenen Logik, damit die Primäraktion auch dann
 * feuert, wenn der Tap auf ein interaktives Kind ging.
 */
export const InfoTip = forwardRef<HTMLSpanElement, InfoTipProps>(function InfoTip(
  {
    text,
    children,
    // Radix' Popover.Content defaultet auf side="bottom", der frühere
    // Tooltip.Content auf "top" — und KEIN Aufrufer setzt die Prop. Der
    // Default hier ist deshalb die einzige Stelle, die alle 13 Blasen oben
    // hält (z.B. die Combo-Erklärung, die sonst über die Slot-Leiste klappt).
    side = "top",
    className,
    onClick,
    onPointerEnter,
    onPointerLeave,
    ...triggerProps
  },
  forwardedRef,
) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const hoverTimerRef = useRef<number | null>(null);

  // Eigener Ref (für die Trefferprüfung unten) UND der von außen gereichte
  // Ref müssen beide gesetzt werden.
  const setAnchor = useCallback(
    (node: HTMLSpanElement | null) => {
      anchorRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef],
  );

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current !== null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  // Der Kampf-Screen montiert/entmontiert Kacheln laufend — offener Timer darf
  // nicht auf eine tote Komponente feuern.
  useEffect(() => clearHoverTimer, [clearHoverTimer]);

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        if (!v) clearHoverTimer();
        setOpen(v);
      }}
    >
      <PopoverAnchor asChild>
        <span
          {...triggerProps}
          ref={setAnchor}
          className={`cursor-help ${className ?? ""}`}
          onPointerEnter={(e) => {
            onPointerEnter?.(e);
            // Nur Maus: Touch löst pointerenter beim Tippen ebenfalls aus und
            // würde das Tap-Toggle unten doppelt schalten.
            if (e.pointerType !== "mouse") return;
            clearHoverTimer();
            hoverTimerRef.current = window.setTimeout(() => setOpen(true), HOVER_OPEN_DELAY_MS);
          }}
          onPointerLeave={(e) => {
            onPointerLeave?.(e);
            if (e.pointerType !== "mouse") return;
            clearHoverTimer();
            setOpen(false);
          }}
          onClick={(e) => {
            // Zuerst der durchgereichte Handler (Radix-Trigger o.ä.) — sonst
            // schluckt der Early-Return unten die Primäraktion.
            onClick?.(e);
            const target = e.target as Element | null;
            const interactive = target?.closest(INTERACTIVE_CHILD) ?? null;
            if (interactive && anchorRef.current?.contains(interactive)) return;
            // Nicht an den darunterliegenden Button/die Karte durchreichen …
            e.stopPropagation();
            // … und kein umgebendes <label> aktivieren (sonst kippt der Tap
            // auf den Erklär-Text die zugehörige Checkbox mit um).
            e.preventDefault();
            clearHoverTimer();
            setOpen((v) => !v);
          }}
        >
          {children}
        </span>
      </PopoverAnchor>
      <PopoverContent
        side={side}
        sideOffset={6}
        collisionPadding={8}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          // Zweiter Tap auf den Trigger: hier NICHT schließen, sonst öffnet
          // der unmittelbar folgende Klick die Blase sofort wieder. Das
          // Schließen übernimmt der Toggle im onClick oben.
          if (anchorRef.current?.contains(e.target as Node)) e.preventDefault();
        }}
        // `pointer-events-none`: die Blase liegt über dem Spielfeld (Slot-
        // Leiste, Checkboxen). Nimmt sie Pointer-Events an, verschluckt sie den
        // Tap auf das verdeckte Element — der erste Tap schließt dann nur die
        // Blase, gezielt wird zweimal. Ohne Pointer-Events geht der Tap direkt
        // an das Element darunter, und Radix' Outside-Handler schließt die
        // Blase im selben Zug.
        className="pointer-events-none w-auto max-w-[min(20rem,calc(100vw-2rem))] px-3 py-2 text-center text-xs leading-relaxed"
      >
        {text}
      </PopoverContent>
    </Popover>
  );
});
